import {
  InputField,
  RulesContent,
  RulesManager,
} from '@carboncopyco/editor-ui';
import type { RulesManagerProps } from '@carboncopyco/editor-ui';
import { FindAndReplaceRuleCreation } from '..';
import type { EditorPerformanceResult } from '@carboncopyco/types';
import { IFindAndReplaceRuleType } from '@carboncopyco/types';
import { useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Collapse,
  ColorHex,
  InputText,
  Loading,
  Panel,
  TextBase,
  TextButton,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { usePerformanceIterations } from './usePerformanceIterations';
import { usePerformanceFindAndReplace } from './usePerformanceFindAndReplace';
import { usePrevious } from 'ahooks';
import { generateCsvFile } from './util';
import { performanceResultsDefault } from '@carboncopyco/utils';
import { cloneDeep } from 'lodash-es';
import { InfoOutlineIcon } from '@carboncopyco/icons';

export interface PerformanceManagerProps
  extends Pick<RulesManagerProps, 'isOpen' | 'onClose'> {
  isEditorLoading: boolean;
  performanceResults: EditorPerformanceResult;
  isPerformanceStarted: boolean;
  performanceSelectorXpath: string;
  onStartStopButtonClick: (
    val: boolean,
    shouldReload: boolean,
    shouldStopOnLoad: boolean
  ) => void;
  onPerformanceResultsAvgCalculated: (result: EditorPerformanceResult) => void;
  onDownloadClick: (csvContent: string) => void;
  onPickSelectorClick: () => void;
  onStopOnSelectorXpathChange: (xpath: string) => void;
  onRestartButtonClick: () => void;
}

export const PerformanceManager = ({
  isOpen,
  isEditorLoading,
  performanceResults,
  isPerformanceStarted,
  performanceSelectorXpath,
  onClose,
  onStartStopButtonClick,
  onPerformanceResultsAvgCalculated,
  onDownloadClick,
  onPickSelectorClick,
  onStopOnSelectorXpathChange,
  onRestartButtonClick,
}: PerformanceManagerProps) => {
  const [numberOfSeconds, setNumberOfSeconds] = useState(0);
  const [numberOfIterations, setNumberOfIterations] = useState(1);
  const [shouldStopOnLoad, setShouldStopOnLoad] = useState(false);
  const [shouldReloadOnStart, setShouldReloadOnStart] = useState(false);
  const [shouldStopOnSelector, setShouldStopOnSelector] = useState(false);

  const prevIsPerformanceStarted = usePrevious(isPerformanceStarted);
  const [prevPerformanceResults, setPrevPerformanceResults] = useState(null);

  const [findAndReplaceAmount, setFindAndReplaceAmout] = useState(10);
  const [ruleToCreate, setRuleToCreate] = useState<IFindAndReplaceRuleType>(
    IFindAndReplaceRuleType.replaceText
  );

  const {
    setNumberOfIterationsCompleted,
    setPerformanceResultsForIterations,
    numberOfIterationsCompleted,
    timeoutRef,
  } = usePerformanceIterations({
    performanceResults,
    isPerformanceStarted,
    numberOfIterations,
    numberOfSeconds,
    shouldStopOnLoad,
    prevIsPerformanceStarted,
    onPerformanceResultsAvgCalculated,
    onRerun: (val) => {
      onStartStopButtonClick(val, shouldReloadOnStart, shouldStopOnLoad);
    },
  });

  const { compareToPrev, createRuleHandler } = usePerformanceFindAndReplace({
    prevPerformanceResults,
    performanceResults,
    findAndReplaceAmount,
  });

  const handleRestartButtonClick = () => {
    setNumberOfIterationsCompleted(0);
    setPerformanceResultsForIterations([]);
    setPrevPerformanceResults(cloneDeep(performanceResultsDefault));
    onRestartButtonClick();
  };

  const handleStartStopButtonClick = () => {
    setNumberOfIterationsCompleted(0);
    setPerformanceResultsForIterations([]);
    clearTimeout(timeoutRef.current);
    const isStarted = !isPerformanceStarted;

    onStartStopButtonClick(isStarted, shouldReloadOnStart, shouldStopOnLoad);
    setPrevPerformanceResults(performanceResults);

    if (Number(numberOfSeconds) > 0 && isStarted && !shouldStopOnLoad) {
      timeoutRef.current = setTimeout(() => {
        onStartStopButtonClick(false, shouldReloadOnStart, shouldStopOnLoad);
      }, Number(numberOfSeconds) * 1000);
    }
  };

  const infoRef = useRef(null);
  return (
    <RulesManager
      isOpen={isOpen}
      onClose={onClose}
    >
      <RulesManager.Header>
        <RulesManager.Header.Title label="Performance manager" />
      </RulesManager.Header>
      <RulesManager.Content>
        <Collapse accordion>
          <Panel header={'Find and replace text'}>
            <InputField
              label="Number of rules"
              name="numberOfrules"
            >
              <div className="flex flex-col gap-1.5">
                <InputText
                  autoFocus
                  size="md"
                  value={String(findAndReplaceAmount)}
                  onChange={({ target }) =>
                    setFindAndReplaceAmout(Number(target.value))
                  }
                />
              </div>
            </InputField>
            <RulesContent.CreationForm show={!!ruleToCreate}>
              <FindAndReplaceRuleCreation
                rules={[]}
                ruleType={ruleToCreate}
                onCreate={createRuleHandler}
                onCancel={() => setRuleToCreate(null)}
              />
            </RulesContent.CreationForm>
          </Panel>
          <div className="flex border-b-ds-natural-150  border-b" />
          <div className="flex items-start mb-4 gap-4">
            <div>
              <InputField
                label="Number of seconds: "
                name="numberOfSeconds"
              >
                <div className="flex gap-1.5 items-center">
                  <InputText
                    size="sm"
                    value={String(numberOfSeconds)}
                    onChange={({ target }) => {
                      setNumberOfSeconds(Number(target.value));
                    }}
                  />
                  <div ref={infoRef}>
                    <div>
                      <InfoOutlineIcon
                        size={16}
                        color={ColorHex.natural500}
                      />
                    </div>
                  </div>
                  <Tooltip
                    anchorRef={infoRef}
                    placement="bottom"
                    offset={[0, 10]}
                    noArrow
                  >
                    <div className="w-60 px-3 py-2 shadow-ds-md rounded leading-4">
                      <TextCaption label="This field is used to determine when measuring will stop automatically. Leave it 0 if you want to stop it manually with Stop button. " />
                    </div>
                  </Tooltip>
                </div>
              </InputField>
              <InputField
                label="Number of iterations: "
                name="numberOfIteration"
              >
                <div className="flex flex-col gap-1.5">
                  <InputText
                    size="sm"
                    value={String(numberOfIterations)}
                    onChange={({ target }) =>
                      setNumberOfIterations(Number(target.value))
                    }
                  />
                </div>
              </InputField>
            </div>
          </div>
          <div className="flex mb-2 flex-col">
            <Checkbox
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Reload page on start"
                />
              }
              name="reload-on-start"
              onChange={(e) => {
                setShouldReloadOnStart(e.target.checked);
              }}
              checked={shouldReloadOnStart}
            />
            <Checkbox
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Stop on window load event"
                />
              }
              name="stop-on-load"
              onChange={(e) => {
                setShouldStopOnLoad(e.target.checked);

                if (e.target.checked) {
                  setShouldStopOnSelector(false);
                }
              }}
              checked={shouldStopOnLoad}
            />
            <Checkbox
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Stop on selector"
                />
              }
              name="stop-on-selector"
              onChange={(e) => {
                setShouldStopOnSelector(e.target.checked);

                if (e.target.checked) {
                  setShouldStopOnLoad(false);
                }
              }}
              checked={shouldStopOnSelector}
            />
            {shouldStopOnSelector && (
              <div className="flex flex-col gap-2">
                <InputField
                  label="Xpath of selector: "
                  name="selectorXpath"
                >
                  <div className="flex flex-col gap-1.5">
                    <InputText
                      size="sm"
                      value={performanceSelectorXpath}
                      onChange={({ target }) => {
                        onStopOnSelectorXpathChange(target.value);
                      }}
                    />
                  </div>
                </InputField>
                <div>
                  <Button
                    xSmall
                    label={'Pick selector'}
                    onClick={onPickSelectorClick}
                  />
                </div>
              </div>
            )}
            <div className="flex mt-5 border-b-ds-natural-150  border-b" />
            <div className="w-full flex flex-col items-center mt-3 justify-center ">
              <div className="flex gap-2">
                <Button
                  disabled={!isEditorLoading}
                  label={isPerformanceStarted ? 'Stop' : 'Start'}
                  onClick={handleStartStopButtonClick}
                />
                <Button
                  label={'Restart'}
                  onClick={handleRestartButtonClick}
                />
              </div>
            </div>
            <div className="flex items-center justify-center pt-2">
              <TextButton
                label={`Iterations completed: ${numberOfIterationsCompleted}`}
                color="text-ds-natural-600"
              />
            </div>
          </div>
          <div className="flex mt-2 mb-2 border-b-ds-natural-150 border-b" />

          {isPerformanceStarted && (
            <div className="f w-full flex items-center justify-center gap-4">
              <Loading />
              <TextButton label="Loading" />
            </div>
          )}
          {!isPerformanceStarted && (
            <div className="flex items-center justify-center mb-4 flex-col gap-4 ">
              <div className="flex flex-col w-full">
                <TextButton
                  label="Number of Nodes: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <TextCaption label={`Div: ${performanceResults.nodes.div}`} />
                  <TextCaption
                    label={`Span: ${performanceResults.nodes.span}`}
                  />
                  <TextCaption
                    label={`Input: ${performanceResults.nodes.input}`}
                  />
                  <TextCaption
                    label={`Button: ${performanceResults.nodes.button}`}
                  />
                  <TextCaption
                    label={`Link: ${performanceResults.nodes.link}`}
                  />
                  <TextCaption
                    label={`Image: ${performanceResults.nodes.image}`}
                  />
                  <TextCaption
                    label={`Iframe/Frame: ${performanceResults.nodes.iframe}`}
                  />
                  <TextCaption label={`P: ${performanceResults.nodes.p}`} />
                  <TextCaption label={`H: ${performanceResults.nodes.h}`} />
                  <TextCaption label="-----------------------------" />
                  <TextCaption
                    label={`Total nodes: ${performanceResults.nodes.total}`}
                  />
                </div>
              </div>
              <div className="flex flex-col w-full ">
                <TextButton
                  label="Find and Replace: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <span>
                    <TextCaption
                      label={`Text: ${performanceResults.searchAndReplace.text.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'text')}
                  </span>
                  <span>
                    <TextCaption
                      label={`By Attribute: ${performanceResults.searchAndReplace.byAttribute.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'byAttribute')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Input: ${performanceResults.searchAndReplace.input.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'input')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Recursive on Shadow Root: ${performanceResults.searchAndReplace.recursiveOnShadowRoot.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'recursiveOnShadowRoot')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Entire Doc: ${performanceResults.searchAndReplace.entireDoc.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'entireDoc')}
                  </span>
                  <TextCaption label="-----------------------------" />
                  <span>
                    <TextCaption
                      label={`Total time: ${performanceResults.searchAndReplace.total.toFixed(2)}ms`}
                    />
                    {compareToPrev('searchAndReplace', 'total')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col w-full ">
                <TextButton
                  label="Rules: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <span>
                    <TextCaption
                      label={`Text: ${performanceResults.rules.text.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'text')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Input: ${performanceResults.rules.input.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'input')}
                  </span>
                  <span>
                    <TextCaption
                      label={`List: ${performanceResults.rules.list.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'list')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Link: ${performanceResults.rules.link.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'link')}
                  </span>

                  <span>
                    <TextCaption
                      label={`Image: ${performanceResults.rules.image.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'image')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Lock: ${performanceResults.rules.lock.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'lock')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Unlock: ${performanceResults.rules.unlock.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'unlock')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Visibility: ${performanceResults.rules.visibility.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'visibility')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Style: ${performanceResults.rules.style.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'style')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Delete: ${performanceResults.rules.delete.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'delete')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Chart: ${performanceResults.rules.chart.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'chart')}
                  </span>
                  <TextCaption label="-----------------------------" />
                  <span>
                    <TextCaption
                      label={`Total time: ${performanceResults.rules.total.toFixed(2)}ms`}
                    />
                    {compareToPrev('rules', 'total')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col w-full ">
                <TextButton
                  label="Modifications: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <span>
                    <TextCaption
                      label={`Guidance tooltips: ${performanceResults.modifications.tooltip.toFixed(2)}ms`}
                    />
                    {compareToPrev('modifications', 'tooltip')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Block interactions: ${performanceResults.modifications.block_interactions.toFixed(2)}ms`}
                    />
                    {compareToPrev('modifications', 'block_interactions')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Replace link: ${performanceResults.modifications.replace_link.toFixed(2)}ms`}
                    />
                    {compareToPrev('modifications', 'replace_link')}
                  </span>
                  <TextCaption label="-----------------------------" />
                  <span>
                    <TextCaption
                      label={`Total time: ${performanceResults.modifications.total.toFixed(2)}ms`}
                    />
                    {compareToPrev('modifications', 'total')}
                  </span>
                </div>
              </div>
              <div className="flex flex-col w-full ">
                <TextButton
                  label="Mutations: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <span>
                    <TextCaption
                      label={`Callback called: ${performanceResults.mutations.callbackCalled}`}
                    />
                    {compareToPrev('mutations', 'callbackCalled', false)}
                  </span>
                  <span>
                    <TextCaption
                      label={`Callback time: ${performanceResults.mutations.callbackTime.toFixed(2)}ms`}
                    />
                    {compareToPrev('mutations', 'callbackTime')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Total number: ${performanceResults.mutations.total}`}
                    />
                    {compareToPrev('mutations', 'total', false)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col w-full ">
                <TextButton
                  label="Cache: "
                  color="text-ds-natural-600"
                />
                <div className="flex flex-col pl-2">
                  <span>
                    <TextCaption
                      label={`Condense whitespace - items added: ${performanceResults.cache.condenseWhiteSpace}`}
                    />
                    {compareToPrev('cache', 'condenseWhiteSpace', false)}
                  </span>
                  <span>
                    <TextCaption
                      label={`Condense whitespace -cache cleared: ${performanceResults.cache.condenseWhiteSpaceClear}`}
                    />
                    {compareToPrev('cache', 'condenseWhiteSpaceClear')}
                  </span>
                  <span>
                    <TextCaption
                      label={`Text content selector resolver - items added: ${performanceResults.cache.textContentSelectorResolver}`}
                    />
                    {compareToPrev(
                      'cache',
                      'textContentSelectorResolver',
                      false
                    )}
                  </span>
                  <span>
                    <TextCaption
                      label={`Text content selector resolver - cache cleared: ${performanceResults.cache.textContentSelectorResolverClear}`}
                    />
                    {compareToPrev('cache', 'textContentSelectorResolverClear')}
                  </span>
                </div>
              </div>
              <Button
                label="Download CSV Report"
                onClick={() =>
                  onDownloadClick(
                    generateCsvFile(
                      performanceResults,
                      shouldReloadOnStart,
                      shouldStopOnLoad,
                      numberOfIterations,
                      numberOfSeconds
                    )
                  )
                }
              />
            </div>
          )}
        </Collapse>
      </RulesManager.Content>
    </RulesManager>
  );
};
